import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Container, Paper, TextField, Typography } from "@mui/material";
import { fetchProfile, requestCode, saveToken, verifyCode, verifySecondFactor } from "../auth/session";

// [M-ALIGN-001 · SEC-AC] Entrar con la cuenta de Fluxi: correo → código → (verificación en dos pasos,
// si la cuenta la tiene) → solo si la cuenta es ADMIN queda abierta la sesión del panel.
const STEPS = {
  email: { title: "Entra con tu cuenta de Fluxi", field: "Correo", action: "Enviar código" },
  code: { title: "Revisa tu correo", field: "Código", action: "Entrar" },
  second: { title: "Verificación en dos pasos", field: "Código de tu app de autenticación", action: "Entrar" },
};

function Login({ onLogin }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [value, setValue] = useState("");
  const [challenge, setChallenge] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const openSession = async (token) => {
    const profile = await fetchProfile(token);
    if (!profile.ok) return setError("No se pudo abrir la sesión. Intenta de nuevo.");
    if (!profile.isAdmin) return setError("Esta cuenta no tiene acceso de administrador.");
    saveToken(token);
    onLogin(profile.email);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (step === "email") {
        const sent = await requestCode(email.trim().toLowerCase());
        if (!sent.ok) {
          return setError(sent.status === 429 ? "Demasiados intentos. Espera un momento." : "No se pudo enviar el código. Intenta de nuevo.");
        }
        setEmail(email.trim().toLowerCase());
        setValue("");
        return setStep("code");
      }
      const answer = step === "code" ? await verifyCode(email, value.trim()) : await verifySecondFactor(challenge, value.trim());
      if (answer.ok && answer.body.requires_2fa) {
        setChallenge(answer.body.challenge);
        setValue("");
        return setStep("second");
      }
      if (!answer.ok || !answer.body.access_token) {
        return setError(answer.status === 429 ? "Demasiados intentos. Espera un momento." : "El código no es válido o ya venció.");
      }
      await openSession(answer.body.access_token);
    } catch {
      setError("No hay conexión con Fluxi. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep("email");
    setValue("");
    setChallenge("");
    setError("");
  };

  const copy = STEPS[step];
  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: step === "code" ? 0.5 : 2 }}>
          {copy.title}
        </Typography>
        {step === "code" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enviamos un código a {email}
          </Typography>
        )}
        <Box component="form" onSubmit={submit}>
          <TextField
            key={step}
            fullWidth
            required
            autoFocus
            margin="normal"
            label={copy.field}
            type={step === "email" ? "email" : "text"}
            autoComplete={step === "email" ? "email" : "one-time-code"}
            inputProps={step === "email" ? {} : { inputMode: step === "code" ? "numeric" : "text" }}
            value={step === "email" ? email : value}
            onChange={(e) => (step === "email" ? setEmail(e.target.value) : setValue(e.target.value))}
          />
          <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 2, py: 1.2 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : copy.action}
          </Button>
          {step !== "email" && (
            <Button fullWidth onClick={restart} disabled={loading} sx={{ mt: 1 }}>
              Usar otro correo
            </Button>
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
}

export default Login;
